import { Inject, Injectable, OnDestroy } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import {
  GetAdminTrackingInfoGQL,
  GetBeneficiaryTrackingInfoGQL,
  GetManagerTrackingInfoGQL,
} from '../../generated/types';
import { mergeMap, switchMap, take } from 'rxjs/operators';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  GladyTrackingSetUserPropertiesEvent,
  TrackingService,
} from '@onep/front-common-lib/tracking';
import { PortalTrackingService } from '@onep/front-portal-lib';
import {
  CURRENT_ORGANIZATION_MANAGEMENT_SERVICE,
  CurrentOrganizationManagementService,
} from '../organization/current-organization-management.service';

const windowWithDataLayer: Window & { dataLayer?: any[] } = window;

@Injectable({
  providedIn: 'root',
})
export class SharedInfoTrackingService implements OnDestroy {
  private trackingSent = false;

  private roleArray = ['Manager', 'Beneficiary', 'Admin', 'ManagerProxi'];

  private allSubscriptions = new Subscription();

  constructor(
    private getBeneficiaryTrackingInfoGql: GetBeneficiaryTrackingInfoGQL,
    private getManagerTrackingInfoGQL: GetManagerTrackingInfoGQL,
    private getAdminTrackingInfoGQL: GetAdminTrackingInfoGQL,
    private oidc: OidcSecurityService,
    private tracking: TrackingService,
    private portalTracking: PortalTrackingService,
    @Inject(CURRENT_ORGANIZATION_MANAGEMENT_SERVICE)
    private currentOrganizationManagement: CurrentOrganizationManagementService
  ) {}

  ngOnDestroy(): void {
    this.allSubscriptions.unsubscribe();
  }

  initTracking(): void {
    this.tracking.initRouterTracking();

    // Send all events received through PortalTrackingService from other microfronts.
    this.allSubscriptions.add(
      this.portalTracking.trackingReceiver$.subscribe((data) => {
        this.tracking.sendEvent(data);
      })
    );
  }

  sendUserTrackingInfo(): void {
    if (this.trackingSent) {
      return;
    }
    windowWithDataLayer.dataLayer = windowWithDataLayer.dataLayer || [];

    if (environment.platform === 'beneficiary') {
      this.getUserId()
        .pipe(
          mergeMap((res) =>
            this.getBeneficiaryTrackingInfoGql.fetch({ beneficiaryId: res.sub })
          )
        )
        .subscribe(({ data }) => {
          const genericEvent: any = this.createUserPropertiesBaseEvent(
            data.authenticatedUser.id,
            null,
            data.beneficiary
          );
          const eventToSend = {
            ...genericEvent,
          };
          this.tracking.sendEvent(
            new GladyTrackingSetUserPropertiesEvent(eventToSend)
          );
        });
    } else if (environment.platform === 'manager') {
      const userData$ = this.getUserId().pipe(
        switchMap((res) =>
          this.getManagerTrackingInfoGQL.fetch({
            beneficiaryId: res.sub,
            locale: 'fr_FR',
          })
        ),
        take(1)
      );
      const orgId$ =
        this.currentOrganizationManagement.currentOrganizationId$.pipe(take(1));

      combineLatest([userData$, orgId$]).subscribe(([{ data }, companyId]) => {
        const genericEvent: any = this.createUserPropertiesBaseEvent(
          data.authenticatedUser.id,
          companyId,
          data.beneficiary
        );
        const eventToSend = {
          ...genericEvent,
        };
        const company = data.authenticatedUser?.memberOf?.filter(
          (m) => m.id === companyId
        )[0];
        if (company) {
          eventToSend.company = {
            company_name: company?.name,
            company_ids: company?.id,
            company_numbers: data.authenticatedUser?.memberOf?.length ?? 0,
            company_typology: company?.typology ?? '',
            company_segment: company?.segment ?? '',
          };
          let modulesName: string[] = [];
          let modulesIds: string[] = [];
          if (company?.subscribedModules) {
            modulesName = company.subscribedModules.map((m) => m.publicName);
            modulesIds = company.subscribedModules.map((m) => m.id);
          }
          eventToSend.modules = {
            modules_ids: modulesIds,
            modules_name: modulesName,
            modules_activated_number: company?.subscribedModules?.length ?? 0,
          };
        }
        this.tracking.sendEvent(
          new GladyTrackingSetUserPropertiesEvent(eventToSend)
        );
      });
    } else if (environment.platform === 'admin') {
      this.getUserId()
        .pipe(
          mergeMap((res) =>
            this.getAdminTrackingInfoGQL.fetch({ beneficiaryId: res.sub })
          )
        )
        .subscribe(({ data }) => {
          const genericEvent = this.createUserPropertiesBaseEvent(
            data.authenticatedUser.id,
            null,
            data.beneficiary
          );
          this.tracking.sendEvent(
            new GladyTrackingSetUserPropertiesEvent(genericEvent)
          );
        });
    }
  }

  getUserId(): Observable<{ sub: string }> {
    return this.oidc.userData$;
  }

  // tslint:disable-next-line:typedef
  createUserPropertiesBaseEvent(
    userId: string,
    companyId: string | null,
    beneficiary: {
      __typename?: 'Beneficiary';
      allRoles?: Array<{
        __typename?: 'UserRole';
        name: string;
        organization?: { __typename?: 'Organization'; id: string } | null;
      }> | null;
    }
  ) {
    let roles = beneficiary.allRoles ? beneficiary.allRoles : [];
    if (beneficiary.allRoles) {
      if (companyId) {
        roles = roles.filter(
          (t) =>
            (t.organization && t.organization?.id === companyId) ||
            t.organization === null
        );
      }
    }
    const rolesStr = roles
      .filter((t) => this.roleArray.includes(t.name))
      .map((t) => t.name.toLowerCase());
    const distinctRoles = rolesStr.filter(
      (value, index) => rolesStr.indexOf(value) === index
    );
    return {
      user: {
        user_id: userId,
        user_type: distinctRoles,
      },
    };
  }
}
